import React, { useState, useEffect, useCallback } from 'react';
import SummaryApi from '../common';
import * as XLSX from 'xlsx';
import { 
  Download, 
  PlusCircle, 
  LayoutList, 
  RefreshCw, 
  Ticket, 
  CheckCircle, 
  BarChart3, 
  Building2, 
  Layers,
  ArrowRight
} from 'lucide-react';

const EntryCouponCreate = () => {
  // Form States
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [orgSubId, setOrgSubId] = useState('');
  const [batchName, setBatchName] = useState('');
  const [totalCodes, setTotalCodes] = useState('');
  const [isActive, setIsActive] = useState(1);
  const [couponSummary, setCouponSummary] = useState([]);
  const [subscriptionDetails, setSubscriptionDetails] = useState({
    planName: '',
    packageName: ''
  });

  // UI States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tableLoading, setTableLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  // 1. Fetch Data
  const fetchCouponSummary = useCallback(async () => {
    setTableLoading(true);
    try {
      const res = await fetch(SummaryApi.getBulkCouponBatches.url, { 
        method: SummaryApi.getBulkCouponBatches.method 
      });
      const result = await res.json();
      if (result.status) setCouponSummary(result.data);
    } catch (err) {
      console.error("Summary fetch failed", err);
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch(SummaryApi.getActiveOrganization.url, {
      method: SummaryApi.getActiveOrganization.method,
      headers: { 'Content-Type': 'application/json' }
    })
      .then(res => res.json())
      .then(res => {
        if (res.status) setOrganizations(res.data);
      })
      .catch(err => console.error("Error fetching organizations:", err));

    fetchCouponSummary();
  }, [fetchCouponSummary]);

  // 2. Handle Logic
  const handleOrgChange = async (e) => {
    const orgId = e.target.value;
    setSelectedOrgId(orgId);
    setOrgSubId('');
    setError('');

    if (!orgId) return;

    try {
      const response = await fetch(SummaryApi.getActiveOrganizationSubscription.url, {
        method: SummaryApi.getActiveOrganizationSubscription.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: orgId })
      });
      const result = await response.json();

      if (result.status && result.data.length > 0) {
        const sub = result.data[0];
        setOrgSubId(sub.organization_subscription_id);
        setSubscriptionDetails({
          planName: sub.plan_name,
          packageName: sub.package_name
        });
      } else {
        setError("No active subscription found.");
        setSubscriptionDetails({ planName: '', packageName: '' });
      }
    } catch (err) {
      setError("Failed to fetch subscription.");
    }
  };

  const handleDownloadExcel = async (orgIdFromTable = null) => {
    const targetOrgId = orgIdFromTable || selectedOrgId;
    if (!targetOrgId) return;

    setDownloadingId(targetOrgId);
    try {
      const response = await fetch(SummaryApi.getCouponsByOrg.url, {
        method: SummaryApi.getCouponsByOrg.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: targetOrgId })
      });

      const result = await response.json();
      if (result.status && Array.isArray(result.data) && result.data.length > 0) {
        const excelData = result.data.map((item, index) => ({
          "S.No.": index + 1,
          "Batch Name": item.batch_name || "N/A",
          "Coupon Code": item.entry_coupon_code || "N/A",
          "Usage Status": item.is_used === 1 ? "Used" : "Unused",
          "Active Status": item.is_active === 1 ? "Active" : "Inactive",
          "Created Date": item.created_date ? new Date(item.created_date).toLocaleDateString() : "N/A"
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Coupons");
        const orgObj = organizations.find(o => o.organization_id == targetOrgId);
        XLSX.writeFile(workbook, `${orgObj?.organization_name || 'Org'}_Coupons.xlsx`);
      }
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orgSubId) return;

    setLoading(true);
    try {
      const response = await fetch(SummaryApi.createBulkEntryCoupon.url, {
        method: SummaryApi.createBulkEntryCoupon.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_subscription_id: parseInt(orgSubId),
          batch_name: batchName,
          total_codes: parseInt(totalCodes),
          is_active: isActive
        })
      });
      const result = await response.json();

      if (result.status) {
        await fetch(SummaryApi.createEntryCoupon.url, {
          method: SummaryApi.createEntryCoupon.method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bulk_coupon_batch_id: result.bulk_coupon_batch_id })
        });
        setBatchName('');
        setTotalCodes('');
        fetchCouponSummary();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 bg-[#fff5f8] min-h-screen">
      <div className="max-w-7xl mx-auto space-y-10">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">
              Bulk Coupon <span className="text-pink-600 underline decoration-pink-200 underline-offset-4">Management</span>
            </h1>
            <p className="text-slate-500 text-sm font-medium max-w-2xl">
              Create, track, and export organization entry coupons in bulk with real-time statistics.
            </p>
          </div>
          
          <button
            onClick={() => handleDownloadExcel()}
            disabled={downloadingId !== null || !selectedOrgId}
            className={`flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-bold transition-all shadow-sm active:scale-95 ${
              !selectedOrgId 
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
              : 'bg-white text-pink-600 border border-pink-100 hover:bg-pink-50 hover:border-pink-300'
            }`}
          >
            <Download size={18} />
            {downloadingId === selectedOrgId ? "Exporting..." : "Export Selected Org"}
          </button>
        </div>

        {/* Main Creation Card */}
        <div className="bg-white rounded-[2rem] shadow-xl shadow-pink-900/5 border border-pink-50 overflow-hidden">
          <div className="px-10 py-6 bg-gradient-to-r from-white to-pink-50/30 border-b border-pink-50 flex items-center gap-3">
            <div className="p-2.5 bg-pink-100 text-pink-600 rounded-xl">
              <PlusCircle size={22} />
            </div>
            <h2 className="text-xl font-bold text-slate-800">Generate New Batch</h2>
          </div>

          <form onSubmit={handleSubmit} className="p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8">
              
              {/* Org Selection */}
              <div className="lg:col-span-4 space-y-2">
                <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">
                  <Building2 size={14}/> Organization
                </label>
                <select
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-medium focus:ring-4 focus:ring-pink-100 focus:border-pink-300 outline-none transition-all appearance-none cursor-pointer"
                  value={selectedOrgId}
                  onChange={handleOrgChange}
                  required
                >
                  <option value="">Select Organization</option>
                  {organizations.map(org => (
                    <option key={org.organization_id} value={org.organization_id}>{org.organization_name}</option>
                  ))}
                </select>
              </div>

              {/* Plan Preview */}
              <div className="lg:col-span-4 space-y-2">
                <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">
                  <Layers size={14}/> Active Plan Info
                </label>
                <div className={`w-full p-4 border rounded-2xl text-sm font-bold flex items-center transition-all ${
                  subscriptionDetails.planName 
                  ? 'bg-pink-50 border-pink-200 text-pink-700 shadow-inner' 
                  : 'bg-slate-50 border-slate-100 text-slate-400 italic'
                }`}>
                  {subscriptionDetails.planName 
                    ? `${subscriptionDetails.packageName} — ${subscriptionDetails.planName}` 
                    : "No active plan found"}
                </div>
              </div>

              {/* Batch Name */}
              <div className="lg:col-span-4 space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Batch Label</label>
                <input
                  type="text" value={batchName} onChange={(e) => setBatchName(e.target.value)}
                  placeholder="e.g. Winter Sale 2024"
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-medium focus:ring-4 focus:ring-pink-100 focus:border-pink-300 outline-none transition-all"
                  required
                />
              </div>

              {/* Total Codes */}
              <div className="lg:col-span-4 space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Coupon Quantity</label>
                <input
                  type="number" value={totalCodes} onChange={(e) => setTotalCodes(e.target.value)}
                  placeholder="0"
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-medium focus:ring-4 focus:ring-pink-100 focus:border-pink-300 outline-none transition-all"
                  required
                />
              </div>

              {/* Toggle & Submit */}
              <div className="lg:col-span-8 flex flex-col sm:flex-row items-center justify-between gap-6 pt-4">
                <label className="relative inline-flex items-center cursor-pointer group">
                  <input type="checkbox" className="sr-only peer" checked={isActive === 1} onChange={(e) => setIsActive(e.target.checked ? 1 : 0)} />
                  <div className="w-14 h-7 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-[20px] after:w-[20px] after:transition-all peer-checked:bg-pink-600 shadow-inner"></div>
                  <span className="ml-4 text-sm font-bold text-slate-600 group-hover:text-pink-600 transition-colors">Enabled for Distribution</span>
                </label>

                <button
                  type="submit"
                  disabled={loading || !orgSubId}
                  className={`px-10 py-4 rounded-2xl text-white font-black text-sm uppercase tracking-widest shadow-lg transition-all flex items-center gap-3 active:scale-95 ${
                    loading || !orgSubId 
                    ? 'bg-slate-300 cursor-not-allowed shadow-none' 
                    : 'bg-[#d32f2f] hover:bg-black hover:shadow-pink-200'
                  }`}
                >
                  {loading ? (
                    <RefreshCw size={18} className="animate-spin" />
                  ) : (
                    <>Create Coupons <ArrowRight size={18}/></>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Statistics Table Card */}
        <div className="bg-white rounded-[2rem] shadow-xl shadow-pink-900/5 border border-pink-50 overflow-hidden">
          <div className="px-10 py-7 border-b border-pink-50 flex justify-between items-center bg-white">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl"><LayoutList size={22} /></div>
              <div>
                <h2 className="text-xl font-bold text-slate-800 tracking-tight">Organization Statistics</h2>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-0.5">Live Usage Monitoring</p>
              </div>
            </div>
            <button 
              onClick={fetchCouponSummary} 
              className="p-3 hover:bg-slate-50 rounded-2xl transition-all border border-transparent hover:border-slate-100 group"
            >
              <RefreshCw size={20} className={`text-slate-400 group-hover:text-pink-500 transition-colors ${tableLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/60 text-[10px] uppercase tracking-[0.15em] font-black text-slate-400">
                  <th className="px-10 py-6 border-b border-slate-100">#</th>
                  <th className="px-10 py-6 border-b border-slate-100">Organization Name</th>
                  <th className="px-10 py-6 border-b border-slate-100">
                    <div className="flex items-center gap-2"><Ticket size={14} className="text-slate-300"/> Total Codes</div>
                  </th>
                  <th className="px-10 py-6 border-b border-slate-100">
                    <div className="flex items-center gap-2"><CheckCircle size={14} className="text-slate-300"/> Redeemed</div>
                  </th>
                  <th className="px-10 py-6 border-b border-slate-100">
                    <div className="flex items-center gap-2"><BarChart3 size={14} className="text-slate-300"/> Balance</div>
                  </th>
                  <th className="px-10 py-6 border-b border-slate-100 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {couponSummary.map((item, index) => {
                  const orgObj = organizations.find(o => o.organization_id === item.organization_id);
                  return (
                    <tr key={item.organization_id} className="hover:bg-pink-50/30 transition-colors group">
                      <td className="px-10 py-6 text-sm font-bold text-slate-300 tracking-tighter">
                        {String(index + 1).padStart(2, '0')}
                      </td>
                      <td className="px-10 py-6">
                        <div className="text-base font-extrabold text-slate-700 group-hover:text-pink-600 transition-colors">
                          {orgObj?.organization_name || "Unknown Entity"}
                        </div>
                      </td>
                      <td className="px-10 py-6">
                        <span className="text-sm font-black text-slate-600 bg-slate-100/80 px-3 py-1.5 rounded-lg border border-slate-200/50 italic">
                          {item.total_counts.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-10 py-6">
                        <span className="px-3.5 py-1.5 bg-emerald-50 text-emerald-600 rounded-xl text-[11px] font-black border border-emerald-100 shadow-sm">
                          {item.used_counts} Used
                        </span>
                      </td>
                      <td className="px-10 py-6">
                        <span className="px-3.5 py-1.5 bg-pink-100 text-pink-600 rounded-xl text-[11px] font-black border border-pink-200">
                          {item.balance_counts} Left
                        </span>
                      </td>
                      <td className="px-10 py-6 text-right">
                        <button 
                          onClick={() => handleDownloadExcel(item.organization_id)} 
                          disabled={downloadingId === item.organization_id} 
                          className="inline-flex items-center gap-2 px-6 py-2.5 bg-slate-800 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-pink-600 hover:shadow-lg hover:shadow-pink-200 transition-all disabled:bg-slate-200 disabled:text-slate-400"
                        >
                          {downloadingId === item.organization_id ? <RefreshCw size={12} className="animate-spin" /> : <Download size={14} />} 
                          Export Coupons
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {couponSummary.length === 0 && (
              <div className="py-20 text-center flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center">
                  <LayoutList className="text-slate-200" size={32}/>
                </div>
                <p className="text-slate-400 font-bold italic text-sm tracking-wide">No organization data discovered yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntryCouponCreate;